import React, { useState } from 'react'
import Modal, { ModalProps } from 'components/modal'
import { useAuth } from 'hooks/auth'
import api, { APIError } from 'helpers/api'
import { getErrorSupportMetadata } from 'helpers/apiError'
import Button from 'components/button'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { useApplicationStore } from 'hooks/application'
import Integration from 'components/integrations'
import { IntegrationProviders, IntegrationSyncResourceTypes } from 'app-constants'
import { IIntegrationSyncResourceTypes, IAutodeskProjectType, IntegrationButtonProps } from 'types'
import {
  getIsProviderSynced,
  syncIntegration
} from 'components/spreadsheet/components/modal/views/integration/helpers/sync'
import useProject from 'hooks/useProject'
import { HUB_BASE } from 'routes'
import { capitaliseEachWord, validateSourceUrl } from 'helpers/utils'

interface IntegrationModalProps extends ModalProps {
  provider: IntegrationProviders
  integrationName: string
  dataType: IIntegrationSyncResourceTypes
}

const IntegrationModal: React.FC<IntegrationModalProps> = ({
  id,
  open,
  setOpen,
  provider,
  integrationName,
  dataType
}) => {
  const { user } = useAuth()
  const { project } = useProject()
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const { setSnackbarMessage, displayErrorMessage } = useApplicationStore()

  const [deletingIntegration, setDeletingIntegration] = useState<boolean>(false)
  const [updateOrDeleteSync, setUpdateOrDeleteSync] = useState<string>()

  const isIntegrationSynced = getIsProviderSynced(provider, spreadsheetData.tableDetails)

  const onFailure = (error: any) => {
    // Roll back the optimistic syncing flag so the banner doesn't linger after a failure.
    setSpreadsheetData({ type: 'UPDATE_TABLE', field: 'isSyncing', value: false })
    displayErrorMessage(error)
  }

  // The import runs in the background. We mark the table as syncing and close the modal
  // straight away, handing off to the table's SyncStatus banner — there is no intermediate
  // "importing" animation. Consistent with the "Create from External Data Sources" flow.
  const startImport = (props: IntegrationButtonProps) => {
    // Guard before flipping the optimistic syncing flag: without a signed-in user
    // `syncIntegration` is a no-op, which would otherwise leave the "importing" banner stuck
    // on forever. And if an import is already running, don't queue a duplicate one.
    if (!user) {
      setSnackbarMessage({ status: 'error', message: 'Sign in to Morta to sync data into this table.' })
      return
    }
    if (spreadsheetData.tableDetails.isSyncing) {
      setSnackbarMessage({
        status: 'error',
        message: 'This table is already syncing. Wait for that sync to finish, then try again.'
      })
      return
    }
    setSpreadsheetData({ type: 'UPDATE_TABLE', field: 'isSyncing', value: true })
    setOpen(false)
    syncIntegration(
      user,
      provider,
      props,
      dataType,
      false,
      () => {},
      spreadsheetData.tableDetails.publicId,
      () => {},
      onFailure,
      isIntegrationSynced
    )
  }

  const handleFailureMessage = (error: string, cause: unknown, tableId?: string, tableViewId?: string) => {
    setSnackbarMessage({
      status: 'error',
      message: error,
      ...getErrorSupportMetadata(cause),
      action:
        tableId && tableViewId ? (
          <a
            className="p-[5px] rounded-sm border border-surface"
            style={{ color: 'inherit !important' }}
            href={`${HUB_BASE}${project.publicId}/table/${tableId}/view/${tableViewId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open Table
          </a>
        ) : (
          <div />
        )
    })
  }

  const deleteIntegration = () => {
    const confirmation = window.confirm('Are you sure you want to delete this integration?')
    if (confirmation) {
      setDeletingIntegration(true)
      api
        .deleteIntegration(spreadsheetData.tableDetails.publicId, provider)
        .then(() => {
          window.location.reload()
        })
        .catch((e) => {
          setDeletingIntegration(false)
          const error = e as APIError
          if (error.errors?.length) {
            const errorEntry = error.errors[0]
            const attributes = errorEntry.attribute
            if (attributes && typeof attributes !== 'string') {
              const tableId = attributes.tableId
              const tableViewId = attributes.tableViewId
              handleFailureMessage(
                Array.isArray(errorEntry.messages) && errorEntry.messages.length > 0
                  ? errorEntry.messages[0]
                  : "This sync couldn't be deleted. Try again.",
                error,
                tableId,
                tableViewId
              )
            } else {
              handleFailureMessage(
                Array.isArray(errorEntry.messages) && errorEntry.messages.length > 0
                  ? errorEntry.messages[0]
                  : "This sync couldn't be deleted. Try again.",
                error
              )
            }
          } else {
            displayErrorMessage(e)
          }
        })
    }
  }

  return (
    <Modal
      id={id}
      open={open}
      setOpen={setOpen}
      title={
        isIntegrationSynced && updateOrDeleteSync === 'update'
          ? `Update Import From ${integrationName}`
          : isIntegrationSynced && updateOrDeleteSync === 'delete'
            ? `Delete Import From ${integrationName}`
            : isIntegrationSynced && updateOrDeleteSync === undefined
              ? `Update or Delete Import From ${integrationName}`
              : `Import ${capitaliseEachWord(dataType.replace(/_/g, ' '))} From ${integrationName}`
      }
      warningBanner={
        isIntegrationSynced && (updateOrDeleteSync === undefined || updateOrDeleteSync === 'update') ? false : true
      }
      warningBannerText={
        isIntegrationSynced
          ? 'This will delete all data currently in the table. Please download any data you wish to keep as CSV.'
          : 'You need to delete all rows in the table before setting up the import.'
      }
    >
      <img
        className="mb-[30px] w-[300px]"
        src={validateSourceUrl(`/assets/images/${provider}.webp`)}
        alt={`${provider}`}
      />
      {(!isIntegrationSynced || (isIntegrationSynced && updateOrDeleteSync === 'update')) && (
        <div>
          <div className="mb-4">
            You are {isIntegrationSynced ? 'updating' : 'setting up'} an import from {integrationName}. This will copy
            data from your {integrationName} account into the Morta table.
          </div>
          {provider === IntegrationProviders.AUTODESK_BIM360 &&
            (IntegrationSyncResourceTypes.CHECKLISTS === dataType ||
              IntegrationSyncResourceTypes.REVIEWS === dataType) && (
              <div>
                Morta currently imports {dataType} from{' '}
                {dataType === IntegrationSyncResourceTypes.CHECKLISTS && IAutodeskProjectType.BIM360}
                {dataType === IntegrationSyncResourceTypes.REVIEWS && IAutodeskProjectType.ACC} projects.{' '}
                {dataType === IntegrationSyncResourceTypes.CHECKLISTS && IAutodeskProjectType.ACC}
                {dataType === IntegrationSyncResourceTypes.REVIEWS && IAutodeskProjectType.BIM360} projects will appear
                disabled in the list below.
              </div>
            )}

          {isIntegrationSynced && updateOrDeleteSync === 'update' && (
            <div className="mt-5">
              No data will be deleted from the table. All new data from the updated import will be combined with the
              existing data.
            </div>
          )}

          <Integration
            provider={provider}
            type={dataType}
            buttonLabel={isIntegrationSynced ? 'Update Import' : 'Import Data'}
            buttonLoading={false}
            buttonFunction={(props) => startImport(props)}
            projectIds={spreadsheetData.tableDetails.syncProjectIds}
            viewIds={spreadsheetData.tableDetails.syncViewIds}
            syncAllHubs={spreadsheetData.tableDetails.syncAllHubs}
          />
        </div>
      )}

      {isIntegrationSynced && updateOrDeleteSync === undefined && (
        <div>
          <div className="mb-5">
            Please select whether you would like to update the details of the import, or delete it.
          </div>
          <div className="flex items-center mb-5">
            <Button
              className="text-base ml-auto mr-2.5"
              onClick={() => setUpdateOrDeleteSync('update')}
              internalType="accept"
            >
              Update Import
            </Button>
            <Button onClick={() => setUpdateOrDeleteSync('delete')} className="text-base " internalType="danger">
              Delete Import
            </Button>
          </div>
        </div>
      )}

      {isIntegrationSynced && updateOrDeleteSync === 'delete' && (
        <div>
          <div>
            Are you sure you would like to delete the import from {integrationName}? This will remove all data from this
            table and delete all columns related to your {integrationName} account.
          </div>
          <div className="flex mt-[50px]">
            <Button
              className="text-base ml-auto mr-2.5"
              internalType="accept"
              onClick={() => setUpdateOrDeleteSync(undefined)}
            >
              Go Back
            </Button>
            <Button
              className="text-base"
              internalType="danger"
              onClick={() => deleteIntegration()}
              isLoading={deletingIntegration}
            >
              I understand the consequences, delete the {integrationName} integration
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
export default IntegrationModal
